import { useState, useCallback, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { consignments } from "../services/api";
import type { ConsignmentLookupResult } from "../types";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function formatCurrency(n: number): string {
  return `\u00A3${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function statusBadge(status: string) {
  const map: Record<string, { bg: string; text: string; label: string }> = {
    VERIFIED: {
      bg: "bg-green-100",
      text: "text-green-800",
      label: "Verified",
    },
    ISSUES_FOUND: {
      bg: "bg-red-100",
      text: "text-red-800",
      label: "Issues Found",
    },
    PROCESSING: {
      bg: "bg-amber-100",
      text: "text-amber-800",
      label: "Processing",
    },
    ERROR: {
      bg: "bg-gray-100",
      text: "text-gray-800",
      label: "Error",
    },
  };
  const s = map[status] ?? { bg: "bg-gray-100", text: "text-gray-800", label: status };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${s.bg} ${s.text}`}
    >
      {s.label}
    </span>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function LookupPage() {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const {
    data,
    isLoading,
    error,
    isFetched,
  } = useQuery<ConsignmentLookupResult>({
    queryKey: ["consignment", searchTerm],
    queryFn: () => consignments.lookup(searchTerm),
    enabled: searchTerm.length > 0,
    retry: false,
  });

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      const trimmed = searchInput.trim();
      if (trimmed) {
        setSearchTerm(trimmed);
      }
    },
    [searchInput],
  );

  const hasResults = data && data.instances && data.instances.length > 0;
  const noResults = isFetched && searchTerm && !isLoading && !error && !hasResults;
  const isNotFound = error && (error as { status?: number }).status === 404;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Consignment Lookup
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          Search for a consignment number to see its verification history.
        </p>
      </div>

      {/* ── Search bar ── */}
      <form onSubmit={handleSubmit} className="flex gap-3">
        <div className="relative flex-1">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <svg
              className="h-5 w-5 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
              />
            </svg>
          </div>
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Enter consignment number (e.g. WH400884)"
            className="block w-full rounded-lg border border-gray-300 py-3 pl-10 pr-4 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <button
          type="submit"
          disabled={!searchInput.trim() || isLoading}
          className="rounded-lg bg-blue-600 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
        >
          {isLoading ? "Searching..." : "Search"}
        </button>
      </form>

      {/* ── Loading ── */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <svg
            className="h-6 w-6 animate-spin text-blue-600"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          <span className="ml-2 text-sm text-gray-500">
            Searching for {searchTerm}...
          </span>
        </div>
      )}

      {/* ── Not found ── */}
      {(noResults || isNotFound) && (
        <div className="rounded-xl border border-gray-200 bg-white px-6 py-12 text-center shadow-sm">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
            />
          </svg>
          <p className="mt-4 text-sm font-medium text-gray-900">
            No results found for consignment{" "}
            <span className="font-bold">{searchTerm}</span>
          </p>
          <p className="mt-1 text-sm text-gray-500">
            Check the consignment number and try again.
          </p>
        </div>
      )}

      {/* ── Error (non-404) ── */}
      {error && !isNotFound && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          An error occurred while searching. Please try again.
        </div>
      )}

      {/* ── Results ── */}
      {hasResults && (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-200 px-6 py-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Results for{" "}
              <span className="text-blue-600">{data.consignment_number}</span>
            </h2>
            <p className="mt-0.5 text-sm text-gray-500">
              Found in {data.instances.length} invoice
              {data.instances.length !== 1 ? "s" : ""}
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-xs font-medium uppercase tracking-wide text-gray-500">
                  <th className="px-6 py-3">Invoice #</th>
                  <th className="px-6 py-3">Invoice Date</th>
                  <th className="px-6 py-3">Amount</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.instances.map((instance) => (
                    <tr key={instance.invoice_id} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-6 py-3 font-medium text-gray-900">
                        {instance.invoice_number || "—"}
                      </td>
                      <td className="whitespace-nowrap px-6 py-3 text-gray-700">
                        {instance.invoice_date ? formatDate(instance.invoice_date) : "—"}
                      </td>
                      <td className="whitespace-nowrap px-6 py-3 text-gray-700">
                        {formatCurrency(instance.amount)}
                      </td>
                      <td className="whitespace-nowrap px-6 py-3">
                        {statusBadge(instance.status)}
                      </td>
                      <td className="whitespace-nowrap px-6 py-3">
                        <button
                          onClick={() => navigate(`/invoice/${instance.invoice_id}`)}
                          className="text-sm font-medium text-blue-600 hover:text-blue-700"
                        >
                          View Invoice
                        </button>
                      </td>
                    </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
